import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { P } from '../palette';
import { sans } from '../typography';
import { ic } from '../icons';
import { motion } from '../motion';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  shake?: boolean;
}

export function Modal({ open, onClose, title, children, actions, shake }: ModalProps) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // a11y (UX-03) : focus initial dans la modale + restitution à la fermeture.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => { restoreRef.current?.focus?.(); };
  }, [open]);

  // Piège à focus : Tab reste borné aux éléments focusables de la modale.
  const trapTab = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusables = panelRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const content = isMobile ? (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'hsla(230,18%,6%,0.7)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          animation: `cp-fade-in ${motion.duration.normal} ${motion.easing.default}`,
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={trapTab}
        style={{
          position: 'relative',
          background: P.s2,
          borderRadius: '14px 14px 0 0',
          border: `1px solid ${P.border}`,
          borderBottom: 'none',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          outline: 'none',
          animation: shake
            ? `cp-shake ${motion.duration.slow} ${motion.easing.default}`
            : `cp-slide-up ${motion.duration.normal} ${motion.easing.decelerate}`,
          willChange: 'transform',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: P.t3, opacity: 0.4 }} />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px 12px',
          }}
        >
          <span style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: P.t1 }}>{title}</span>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              background: 'transparent',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={P.t2} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d={ic.x} />
            </svg>
          </button>
        </div>
        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>{children}</div>
        {actions && (
          <div
            style={{
              padding: '12px 16px',
              borderTop: `1px solid ${P.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  ) : (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'hsla(230,18%,6%,0.7)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          animation: `cp-fade-in ${motion.duration.normal} ${motion.easing.default}`,
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={trapTab}
        style={{
          position: 'relative',
          background: P.s2,
          borderRadius: 12,
          border: `1px solid ${P.border}`,
          maxWidth: 480,
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          outline: 'none',
          animation: shake
            ? `cp-shake ${motion.duration.slow} ${motion.easing.default}`
            : `cp-scale-in ${motion.duration.normal} ${motion.easing.spring}`,
          willChange: 'transform, opacity',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 16px 12px',
          }}
        >
          <span style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: P.t1 }}>{title}</span>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              background: 'transparent',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={P.t2} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d={ic.x} />
            </svg>
          </button>
        </div>
        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>{children}</div>
        {actions && (
          <div
            style={{
              padding: '12px 16px',
              borderTop: `1px solid ${P.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
